import React, { useEffect, useMemo, useState } from "react"
import { Alert, Button, Input, InputNumber, Modal, Spin, Tag, message } from "antd"
import StickyTable from "../../components/table/StickyTable"   // tog'a #4695/#4703: hamma jadvalda sarlavha va «Итого» qotadi
import { DeleteOutlined } from "@ant-design/icons"
import { useQueryClient } from "@tanstack/react-query"
import i18n from "../../services/i18n"
import WarehouseSelect from "../../components/form/WarehouseSelect"
import ResidueBatchPicker, { ResidueBatch } from "../../components/form/ResidueBatchPicker"
import { request } from "../../utils/config"
import { apiErrorText } from "../../utils/apiError"
import { EDIT_TRANSFER_DOC, GET_TRANSFER_HISTORY } from "./mutations"
import { useIsMainSystem } from "../../utils/mainSystem"   // tog'a #4104

// tog'a #3177 (2026-10-02): «Ожидание»dagi (hali «Отправить» bosilmagan) perevodni O'ZGARTIRISH — son, qator qo'shish /
// olib tashlash, qabul ombori, izoh. Ochilishi: jadvaldagi ✏️ tugmasi `transfer-edit` oyna-hodisasini yuboradi
// (ustun render funksiyasida hook yo'q), oyna sahifa ichida turadi — react-query va router provayderlari bilan.
// Server baribir hammasini qayta tekshiradi (qoldiq, holat, zakaz uchun kerakli son) — bu yerda faqat qulaylik.

export const openTransferEdit = (record: any) =>
  window.dispatchEvent(new CustomEvent("transfer-edit", { detail: { id: record?.id } }))

type Row = {
  key: string
  id?: number              // mavjud qator (Status 0); yangi qatorda yo'q
  purchaseId: number
  product: string
  article?: string
  batch?: string
  fromWarehouseId?: number
  fromWarehouseName?: string
  sent: number             // serverdagi joriy son (yangi qatorda 0)
  count: number
  available: number        // shu qator uchun eng ko'p son: partiya qoldig'i + hozir zaxirada turgani
  note?: string
  price?: number | null
  status: number           // 0 tahrir qilinadi; 1/2 — faqat ko'rinadi
}

export default function TransferEditModal() {
  const queryClient = useQueryClient()
  const bm = useIsMainSystem()   // tog'a #4104: hamkor tizimida partiya ustuni yo'q
  const [rowId, setRowId] = useState<number | null>(null)
  const [data, setData] = useState<any>(null)
  const [rows, setRows] = useState<Row[]>([])
  const [toWh, setToWh] = useState<number | undefined>()
  const [note, setNote] = useState("")
  const [reason, setReason] = useState("")
  const [err, setErr] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const h = (e: any) => setRowId(Number(e?.detail?.id) || null)
    window.addEventListener("transfer-edit", h)
    return () => window.removeEventListener("transfer-edit", h)
  }, [])

  useEffect(() => {
    if (!rowId) return
    setData(null); setErr(""); setRows([]); setReason("")
    GET_TRANSFER_HISTORY(rowId)
      .then(async (d: any) => {
        const items: any[] = d?.items || []
        const pend = items.filter((x) => Number(x.status) === 0)
        let left = new Map<number, number>()
        try {
          const ids = Array.from(new Set(pend.map((x) => Number(x.purchaseId)).filter(Boolean)))
          if (ids.length) {
            const r = await request({ url: "Residues/BatchesLeft", params: { ids: ids.join(",") } })
            left = new Map(((r as any)?.data?.items || []).map((x: any) => [Number(x.purchaseId), Number(x.amount) || 0]))
          }
        } catch { /* son chegarasi serverda baribir tekshiriladi */ }
        setRows(
          items
            .filter((x) => Number(x.status) !== 3)
            .map((x) => ({
              key: "r" + x.id,
              id: x.id,
              purchaseId: Number(x.purchaseId),
              product: x.product,
              article: x.article,
              batch: x.batchNumber,
              fromWarehouseId: x.fromWarehouseId,
              fromWarehouseName: x.fromWarehouseName,
              sent: Number(x.sent) || 0,
              count: Number(x.sent) || 0,
              available: (Number(x.sent) || 0) + (left.get(Number(x.purchaseId)) || 0),
              note: x.note || "",
              price: x.transferPriceUSD == null ? null : Number(x.transferPriceUSD),
              status: Number(x.status),
            }))
        )
        setToWh(Number(d?.toWarehouseId) || undefined)
        setNote(d?.note || "")
        setData(d)
      })
      .catch((e: any) => setErr(apiErrorText(e)))
  }, [rowId])

  const close = () => { setRowId(null); setData(null) }
  const yuborilgan = rows.some((r) => r.status === 1 || r.status === 2)
  const omborQulf = !!data?.isOrderDoc || yuborilgan

  function addBatches(list: ResidueBatch[]) {
    setRows((prev) => {
      const bor = new Set(prev.map((r) => r.purchaseId))
      const yangi = (list || [])
        .filter((b) => b?.purchaseId && !bor.has(b.purchaseId))
        .map((b) => {
          const av = Number(b.left) || 0
          return {
            key: "n" + b.purchaseId,
            purchaseId: b.purchaseId,
            product: b.name || `#${b.purchaseId}`,
            article: b.article || "",
            batch: b.batchNumber || b.document || "",
            fromWarehouseId: b.warehouseId,
            fromWarehouseName: b.warehouseName,
            sent: 0,
            count: av > 0 ? Math.min(1, av) : 0,
            available: av,
            note: "",
            price: null,
            status: 0,
          } as Row
        })
      return [...prev, ...yangi]
    })
  }
  const upd = (key: string, patch: Partial<Row>) => setRows((p) => p.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  const del = (key: string) => setRows((p) => p.filter((r) => r.key !== key))

  const xato = useMemo(() => {
    if (!rows.length) return i18n.t("trEditKeepOne", "Оставьте хотя бы один товар — чтобы отменить всё перемещение, нажмите «Отменить»")
    const bad = rows.find((r) => r.status === 0 && (!(r.count > 0) || r.count > r.available + 1e-9))
    if (bad) return `«${bad.product}»: ${i18n.t("trEditCountRange", "количество от 0.01 до")} ${Math.round(bad.available * 100) / 100}`
    const same = toWh ? rows.find((r) => r.status === 0 && Number(r.fromWarehouseId) === Number(toWh)) : null
    if (same) return `«${same.product}»: ${i18n.t("trEditSameWh", "товар уже находится на складе-получателе")}`
    return ""
  }, [rows, toWh])

  async function save() {
    if (!data?.docId || xato) return
    setSaving(true)
    try {
      const res: any = await EDIT_TRANSFER_DOC(Number(data.docId), {
        toWarehouseId: omborQulf ? undefined : toWh,
        note,
        reason: reason.trim() || undefined,
        items: rows
          .filter((r) => r.status === 0)
          .map((r) => ({
            id: r.id,
            purchaseId: r.purchaseId,
            transferCount: r.count,
            note: r.note || "",
            transferPriceUSD: r.price == null ? undefined : Number(r.price),
          })),
      })
      message.success(Number(res?.changed) > 0
        ? i18n.t("trEditSaved", "Перемещение изменено") + (res?.transferNumber ? ` (${res.transferNumber})` : "")
        : i18n.t("trEditNoChanges", "Изменений нет"))
      queryClient.invalidateQueries()
      close()
    } catch (e: any) {
      message.error(apiErrorText(e))
    } finally {
      setSaving(false)
    }
  }

  const columns: any[] = [
    { title: i18n.t("product"), dataIndex: "product", render: (v: string, r: Row) => (
      <span>
        <b>{v}</b>{r.article ? <span style={{ color: "#64748b" }}> · {r.article}</span> : null}
        {!r.id ? <Tag color="blue" style={{ marginLeft: 6 }}>{i18n.t("trEditNew", "новый")}</Tag> : null}
        {r.status === 1 ? <Tag color="green" style={{ marginLeft: 6 }}>{i18n.t("inTransitStatus")}</Tag> : null}
        {r.status === 2 ? <Tag style={{ marginLeft: 6 }}>{i18n.t("deliveredStatusM")}</Tag> : null}
      </span>
    ) },
    ...(bm ? [{ title: i18n.t("numberParty"), dataIndex: "batch", width: 110, render: (v: string) => <span style={{ color: "#64748b" }}>{v || "—"}</span> }] : []),   // tog'a #4104: faqat glavniy tizimda
    { title: i18n.t("warehouse"), dataIndex: "fromWarehouseName", width: 120, render: (v: string) => <span style={{ color: "#64748b" }}>{v || "—"}</span> },
    { title: i18n.t("residue"), key: "av", width: 80, render: (_: any, r: Row) => (r.status === 0 ? <b style={{ color: "#0369a1" }}>{Math.round(r.available * 100) / 100}</b> : "—") },
    { title: i18n.t("quantity"), key: "count", width: 140, render: (_: any, r: Row) => (
      r.status !== 0 ? <b>{r.count}</b> : (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <InputNumber
            data-testid="tr-edit-count"
            min={0.01}
            max={r.available}
            step={1}
            value={r.count}
            status={r.count <= 0 || r.count > r.available + 1e-9 ? "error" : undefined}
            onChange={(v) => upd(r.key, { count: Number(v) || 0 })}
            style={{ width: 90 }}
          />
          {r.id && Math.abs(r.count - r.sent) > 1e-9 ? <span style={{ fontSize: 11, color: "#b45309", whiteSpace: "nowrap" }}>{r.sent} →</span> : null}
        </span>
      )
    ) },
    { title: i18n.t("trTransferPrice"), key: "price", width: 120, render: (_: any, r: Row) => (
      r.status !== 0 ? (r.price == null ? "—" : `$${r.price}`) : (
        <InputNumber min={0} max={1000000} step={1} prefix="$" value={r.price as any} style={{ width: "100%" }}
          onChange={(v) => upd(r.key, { price: v == null ? null : Number(v) })} />
      )
    ) },
    { title: i18n.t("note"), key: "note", width: 130, render: (_: any, r: Row) => (
      r.status !== 0 ? (r.note || "—") : <Input size="small" value={r.note} onChange={(e) => upd(r.key, { note: e.target.value })} />
    ) },
    { title: "", key: "act", width: 40, render: (_: any, r: Row) => (
      r.status === 0 ? <Button size="small" type="text" danger icon={<DeleteOutlined />} data-testid="tr-edit-remove" onClick={() => del(r.key)} /> : null
    ) },
  ]

  return (
    <Modal
      visible={!!rowId}
      title={<span>✏️ {i18n.t("trEditTitle", "Изменить перемещение")} {data?.transferNumber ? <b>{data.transferNumber}</b> : null}</span>}
      width={Math.min(1100, typeof window !== "undefined" ? window.innerWidth - 24 : 1100)}
      onCancel={close}
      destroyOnClose
      maskClosable={false}
      zIndex={1010}
      footer={[
        <Button key="c" onClick={close}>{i18n.t("cancel")}</Button>,
        <Button key="s" type="primary" loading={saving} disabled={!data || !!xato} onClick={save} data-testid="tr-edit-save">{i18n.t("save")}</Button>,
      ]}
    >
      {err ? <Alert type="error" showIcon message={err} /> : !data ? (
        <div style={{ textAlign: "center", padding: 24 }}><Spin /></div>
      ) : (
        <div data-testid="tr-edit-modal">
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message={i18n.t("trEditHint", "Изменить можно только товары в статусе «Ожидание» (ещё не отправлены). Склад-получатель получит уведомление, изменения записываются в историю.")} />
          {data?.isOrderDoc && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message={i18n.t("trEditOrderHint", "Перемещение для заказа партнёра: склад-получатель не меняется, количество не может быть меньше, чем нужно для заказа.")} />
          )}
          <div style={{ marginBottom: 10 }}>
            <ResidueBatchPicker onSelectRows={addBatches} />
          </div>
          <StickyTable rowKey="key" size="small" bordered pagination={false} scroll={{ x: "max-content" }} columns={columns} dataSource={rows} />
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 12 }}>
            <div style={{ flex: "1 1 260px" }}>
              <div style={{ marginBottom: 4, fontWeight: 600 }}>{i18n.t("trToWarehouse", "Склад-получатель")}</div>
              <WarehouseSelect
                hasEmpty={false}
                disabled={omborQulf}
                value={toWh as any}
                onChange={(v: any) => setToWh(Number(v) || undefined)}
                style={{ width: "100%" }}
              />
              {omborQulf && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{data?.isOrderDoc ? i18n.t("trEditWhLockOrder", "Для заказа склад-получатель не меняется") : i18n.t("trEditWhLockSent", "Часть товаров уже отправлена — склад-получатель не меняется")}</div>}
            </div>
            <div style={{ flex: "1 1 260px" }}>
              <div style={{ marginBottom: 4, fontWeight: 600 }}>{i18n.t("docNote")}</div>
              <Input.TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div style={{ flex: "1 1 260px" }}>
              <div style={{ marginBottom: 4, fontWeight: 600 }}>{i18n.t("trEditReason", "Причина изменения")}</div>
              <Input.TextArea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={i18n.t("noteOptional")} data-testid="tr-edit-reason" />
            </div>
          </div>
          {xato && <Alert type="error" showIcon style={{ marginTop: 12 }} message={xato} />}
        </div>
      )}
    </Modal>
  )
}
